import { QueryClient } from "react-query";

import { featureFlags } from "../config/featureFlags";
import { BudgetService } from "../services/budget.service";
import { Budget } from "../types/budget/budget";
import { BudgetSummary } from "../types/budget/budget-summary";
import type { BudgetServiceLine } from "../types/budget/budget-service-v2";
import {
  mergeShellWithServices,
  patchSummaryFromBudget,
} from "../utils/budget-adapters";
import { collectServiceUpdatedAts } from "../utils/service-etag";
import type {
  BudgetTotalsResponse,
  GetBudgetTotalsParams,
} from "../types/budget/budget-totals";
import { queryKeys } from "./keys";

/** Keep hydrated editor docs warm across re-select within a session. */
const BUDGET_DETAIL_STALE_MS = 5 * 60 * 1000;

/** Page size when draining `GET /v2/budget/:id/services` for editor bootstrap. */
export const BUDGET_SERVICES_PAGE_LIMIT = 100;

/** Safety cap so a bad `total` cannot loop forever. */
const BUDGET_SERVICES_MAX_PAGES = 1000;

export async function fetchBudgetList(
  queryClient: QueryClient,
  projectId: string
): Promise<Budget[]> {
  const data = await queryClient.fetchQuery(
    queryKeys.budgetList(projectId),
    () => BudgetService.getBudgetList(projectId)
  );
  return Array.isArray(data) ? data : [];
}

export async function fetchBudgetSummaryList(
  queryClient: QueryClient,
  projectId: string
): Promise<BudgetSummary[]> {
  const data = await queryClient.fetchQuery(
    queryKeys.budgetSummaryList(projectId),
    () => BudgetService.getBudgetSummaryList(projectId)
  );
  return Array.isArray(data) ? data : [];
}

/**
 * Drains every page of `GET /v2/budget/:id/services` (no calcOption filter)
 * so the editor receives the full service set before load completes (FE-P3-01).
 */
export async function fetchAllBudgetServicesV2(
  queryClient: QueryClient,
  budgetId: string
): Promise<BudgetServiceLine[]> {
  const all: BudgetServiceLine[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;

  while (all.length < total) {
    if (page > BUDGET_SERVICES_MAX_PAGES) {
      throw new Error(
        `Budget services pagination exceeded max pages for id: ${budgetId}`
      );
    }

    const filters = { page, limit: BUDGET_SERVICES_PAGE_LIMIT };
    const response = await queryClient.fetchQuery(
      queryKeys.budgetServices(budgetId, filters),
      () => BudgetService.getBudgetServicesV2(budgetId, filters),
      { staleTime: BUDGET_DETAIL_STALE_MS }
    );

    const items = Array.isArray(response?.items) ? response.items : [];
    total =
      typeof response?.total === "number"
        ? response.total
        : all.length + items.length;
    all.push(...items);

    if (items.length === 0 || items.length < BUDGET_SERVICES_PAGE_LIMIT) {
      break;
    }
    page += 1;
  }

  return all;
}

/**
 * Full editor document for a budget id, cached per id.
 * Re-selecting the same budget does not hit the network while the query is fresh.
 *
 * - `useV2BudgetDetail` on (FE-P3-01): `GET /v2/budget/:id` shell + drained
 *   `GET /v2/budget/:id/services`, merged via {@link mergeShellWithServices}.
 * - Flag off: v1 `GET /budget/get?id=` only (zero v2 detail requests).
 */
export async function fetchBudgetDetail(
  queryClient: QueryClient,
  budgetId: string
): Promise<Budget> {
  if (featureFlags.useV2BudgetDetail) {
    const data = await queryClient.fetchQuery(
      queryKeys.budgetDetail(budgetId),
      async () => {
        const [shell, services] = await Promise.all([
          queryClient.fetchQuery(
            queryKeys.budgetShell(budgetId),
            () => BudgetService.getBudgetShell(budgetId),
            { staleTime: BUDGET_DETAIL_STALE_MS }
          ),
          fetchAllBudgetServicesV2(queryClient, budgetId),
        ]);

        if (!shell?._id) {
          throw new Error(`Budget shell missing for id: ${budgetId}`);
        }

        // FE-P3-05: seed If-Match tokens separately from editor ServiceData allowlist.
        queryClient.setQueryData(
          queryKeys.budgetServiceUpdatedAts(budgetId),
          Object.fromEntries(collectServiceUpdatedAts(services))
        );

        return mergeShellWithServices(shell, services);
      },
      { staleTime: BUDGET_DETAIL_STALE_MS }
    );

    if (!data?._id) {
      throw new Error(`Budget detail missing for id: ${budgetId}`);
    }
    return data;
  }

  const data = await queryClient.fetchQuery(
    queryKeys.budgetDetail(budgetId),
    () => BudgetService.getBudgetById(budgetId),
    { staleTime: BUDGET_DETAIL_STALE_MS }
  );

  if (!data?._id) {
    throw new Error(`Budget detail missing for id: ${budgetId}`);
  }
  return data;
}

/** Seeds / refreshes the per-id detail cache after a full document is known. */
export function setBudgetDetailCache(
  queryClient: QueryClient,
  budget: Budget
): void {
  if (!budget?._id) return;
  queryClient.setQueryData(queryKeys.budgetDetail(budget._id), budget);
}

/**
 * Server totals slice for a phase (`GET /v2/budget/:id/totals`, FE-P4-01).
 * Used when write responses omit totals or `totalsVersion` is stale.
 */
export async function fetchBudgetTotals(
  queryClient: QueryClient,
  budgetId: string,
  params: GetBudgetTotalsParams
): Promise<BudgetTotalsResponse> {
  const filters = {
    phase: params.phase,
    ...(params.negotiationVersion != null
      ? { negotiationVersion: params.negotiationVersion }
      : {}),
    ...(params.calcOption != null ? { calcOption: params.calcOption } : {}),
  };
  return queryClient.fetchQuery(
    queryKeys.budgetTotals(budgetId, filters),
    () => BudgetService.getBudgetTotals(budgetId, params),
    { staleTime: 0 }
  );
}

/**
 * After save, keep the React Query list cache in sync without a full list refetch.
 *
 * - v1 (`useV2BudgetList` off): replace the matching full {@link Budget} in
 *   `budgetList`.
 * - v2 (`useV2BudgetList` on): patch summary fields only on
 *   `budgetSummaryList` via {@link patchSummaryFromBudget} — never store
 *   `budgetServices` in the summary list cache.
 */
export function patchBudgetInListCache(
  queryClient: QueryClient,
  projectId: string | undefined,
  budgetId: string,
  nextBudget: Budget
): void {
  if (!projectId) return;

  // Orçamento que ainda não está na lista em cache (ex.: cópia recém-duplicada:
  // `createBudget` só atualiza a lista em memória). `setQueryData` renovaria o
  // cache SEM ele, e o próximo `loadBudgetList` (ao voltar de Resumo/Serviços)
  // o perderia por 5 min. Marca a lista como velha para vir do servidor.
  const listKey = featureFlags.useV2BudgetList
    ? queryKeys.budgetSummaryList(projectId)
    : queryKeys.budgetList(projectId);
  const cached = queryClient.getQueryData<Array<{ _id?: string }>>(listKey);
  if (cached?.length && !cached.some((item) => item?._id === budgetId)) {
    void queryClient.invalidateQueries(listKey);
    return;
  }

  if (featureFlags.useV2BudgetList) {
    queryClient.setQueryData<BudgetSummary[]>(
      queryKeys.budgetSummaryList(projectId),
      (prev) => {
        if (!prev?.length) return prev ?? [];
        return prev.map((summary) =>
          summary._id === budgetId
            ? patchSummaryFromBudget(summary, nextBudget)
            : summary
        );
      }
    );
    return;
  }

  queryClient.setQueryData<Budget[]>(
    queryKeys.budgetList(projectId),
    (prev) => {
      if (!prev?.length) return prev ?? [];
      return prev.map((budget) =>
        budget._id === budgetId ? nextBudget : budget
      );
    }
  );
}
